import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FolderOpen, Trash2, Download, FilePlus2, Copy, GitCompare } from "lucide-react";
import { outcomeLabel, outcomeTone } from "@/lib/outcomes";

export default function Cases() {
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cases");
      setCases(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this case? This cannot be undone.")) return;
    try {
      await api.delete(`/cases/${id}`);
      toast.success("Case deleted");
      setCases((c) => c.filter((x) => x.id !== id));
      setSelected((s) => s.filter((x) => x !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dl = async (c) => {
    try {
      const res = await api.get(`/cases/${c.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `RIF_plan_${c.patient_label.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicate = async (c) => {
    try {
      const { data } = await api.post(`/cases/${c.id}/duplicate`);
      toast.success("Case duplicated — opening the copy for edit");
      nav(`/workflow/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 2) return [s[1], id];       // rolling window of 2
      return [...s, id];
    });
  };

  const compare = () => {
    if (selected.length !== 2) return;
    nav(`/compare?a=${selected[0]}&b=${selected[1]}`);
  };

  return (
    <div className="reveal">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Patient cases</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1">Case history</h1>
          <p className="mt-2 text-sm text-slate-600">Every generated protocol is stored privately in your workspace. Tick two cases to compare them side-by-side.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={compare} disabled={selected.length !== 2} data-testid="compare-cases-btn">
            <GitCompare className="h-4 w-4 mr-2" strokeWidth={1.75} /> Compare ({selected.length}/2)
          </Button>
          <Link to="/workflow"><Button className="bg-blue-900 hover:bg-blue-800" data-testid="new-case-btn"><FilePlus2 className="h-4 w-4 mr-2" strokeWidth={1.75} /> New case</Button></Link>
        </div>
      </div>

      <div className="mt-8 border border-slate-200 bg-white rounded-md divide-y divide-slate-200" data-testid="cases-list">
        {loading && <div className="p-6 text-sm text-slate-500">Loading cases…</div>}
        {!loading && cases.length === 0 && (
          <div className="p-10 text-center">
            <FolderOpen className="h-8 w-8 mx-auto text-slate-400" strokeWidth={1.5} />
            <div className="mt-3 font-medium text-slate-900">No cases yet</div>
            <div className="text-sm text-slate-500 mt-1">Start your first case from the workflow.</div>
            <Link to="/workflow" className="inline-block mt-4"><Button className="bg-blue-900 hover:bg-blue-800">Start a case</Button></Link>
          </div>
        )}
        {cases.map((c) => (
          <div key={c.id} className="p-4 flex items-center gap-4">
            <Checkbox
              checked={selected.includes(c.id)}
              onCheckedChange={() => toggleSelect(c.id)}
              data-testid={`case-select-${c.id}`}
              aria-label={`Select ${c.patient_label} for comparison`}
            />
            <Link to={`/workflow/${c.id}`} className="min-w-0 flex-1" data-testid={`case-open-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-slate-900 truncate">{c.patient_label}</div>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${outcomeTone(c.outcome)}`} data-testid={`case-outcome-${c.id}`}>
                  {outcomeLabel(c.outcome)}
                </span>
                {c.referring_doctor && (
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border bg-slate-50 text-slate-700 border-slate-200" data-testid={`case-referrer-${c.id}`}>
                    Ref: {c.referring_doctor}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
              <div className="text-xs text-slate-600 mt-1 line-clamp-1 font-mono">{c.protocol?.diagnosis_summary}</div>
            </Link>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => duplicate(c)} data-testid={`case-duplicate-${c.id}`} title="Duplicate case">
                <Copy className="h-4 w-4" strokeWidth={1.75} />
              </Button>
              <Button size="sm" variant="outline" onClick={() => dl(c)} data-testid={`case-pdf-${c.id}`} title="Download PDF">
                <Download className="h-4 w-4" strokeWidth={1.75} />
              </Button>
              <Button size="sm" variant="outline" onClick={() => del(c.id)} data-testid={`case-delete-${c.id}`} title="Delete">
                <Trash2 className="h-4 w-4 text-red-600" strokeWidth={1.75} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
